<template>
  <v-main>
    <v-container fluid>
      <v-row justify="center">
        <v-col cols="12" md="8">
          <v-card elevation="6" rounded="xxl">
            <v-card-title class="headline primary--text">
              <v-icon class="mr-2">mdi-trophy</v-icon>
              Add Award
            </v-card-title>

            <v-card-text>
              <v-form ref="form" v-model="valid" lazy-validation>
                <v-row>
                  <!-- Employee Selection -->
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="form.user_id"
                      :items="users"
                      item-text="name"
                      item-value="id"
                      label="Employee"
                      :rules="[rules.required]"
                      solo
                      rounded
                      dense
                      required
                      :loading="loadingUsers"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-account</v-icon>
                      </template>
                    </v-select>
                  </v-col>

                  <!-- Award Name -->
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.name"
                      label="Award Name"
                      solo
                      rounded
                      dense
                      :rules="[rules.maxLength(255)]"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-trophy</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <!-- Year -->
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="form.year"
                      :items="yearOptions"
                      item-text="text"
                      item-value="value"
                      label="Year"
                      solo
                      rounded
                      dense
                      :rules="[rules.validYear]"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-calendar</v-icon>
                      </template>
                    </v-select>
                  </v-col>

                  <!-- Image Upload -->
                  <v-col cols="12" md="6">
                    <v-file-input
                      v-model="form.image"
                      label="Award Certificate/Image"
                      solo
                      rounded
                      dense
                      accept="image/*"
                      prepend-icon=""
                      :rules="[rules.imageSize]"
                      @change="handleImageUpload"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon>mdi-image</v-icon>
                      </template>
                    </v-file-input>
                  </v-col>

                  <!-- Image Preview -->
                  <v-col cols="12" v-if="imagePreview">
                    <v-card outlined class="pa-3">
                      <div class="text-subtitle-2 mb-2">Image Preview</div>
                      <v-img
                        :src="imagePreview"
                        max-width="200"
                        max-height="200"
                        contain
                        class="mx-auto"
                      ></v-img>
                      <v-btn
                        small
                        text
                        color="error"
                        @click="removeImage"
                        class="mt-2"
                      >
                        <v-icon left small>mdi-delete</v-icon>
                        Remove Image
                      </v-btn>
                    </v-card>
                  </v-col>

                  <!-- Status -->
                  <v-col cols="12" md="6">
                    <v-switch
                      v-model="form.is_active"
                      label="Active Status"
                      color="success"
                      inset
                    ></v-switch>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>

            <v-card-actions class="px-6 pb-6">
              <v-btn color="grey" text @click="goBack" :disabled="saving">
                <v-icon left>mdi-arrow-left</v-icon>
                Cancel
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                @click="saveAward"
                :loading="saving"
                :disabled="!valid"
              >
                <v-icon left>mdi-content-save</v-icon>
                Save Award
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "AwardCreate",
  data() {
    return {
      valid: false,
      saving: false,
      loadingUsers: false,
      users: [],
      imagePreview: null,

      // Form data
      form: {
        user_id: null,
        name: "",
        year: new Date().getFullYear(),
        image: null,
        image_path: "",
        is_active: true,
      },

      // Year options
      yearOptions: [],

      // Validation rules
      rules: {
        required: (value) => !!value || "This field is required",
        maxLength: (max) => (value) =>
          !value || value.length <= max || `Maximum ${max} characters allowed`,
        validYear: (value) => {
          if (!value) return true;
          const currentYear = new Date().getFullYear();
          return (
            (value >= 1900 && value <= currentYear + 5) ||
            "Please enter a valid year"
          );
        },
        imageSize: (value) => {
          if (!value) return true;
          return value.size <= 5000000 || "Image size should be less than 5MB";
        },
      },
    };
  },

  mounted() {
    this.getUsers();
    this.generateYearOptions();
    this.checkQueryParams();
  },

  methods: {
    async getUsers() {
      this.loadingUsers = true;
      try {
        const response = await axios.get("/users", {
          params: { rowsPerPage: 1000 },
        });

        this.users = response.data.data.map((user) => ({
          id: user.id,
          name: `${user.first_name} ${user.last_name}`.trim() || user.email,
        }));
      } catch (error) {
        console.error("Error fetching users:", error);
        this.$toast.error("Failed to load users");
      } finally {
        this.loadingUsers = false;
      }
    },

    generateYearOptions() {
      const currentYear = new Date().getFullYear();
      const startYear = currentYear - 10;
      const endYear = currentYear + 5;

      this.yearOptions = [];
      for (let year = endYear; year >= startYear; year--) {
        this.yearOptions.push({
          text: year.toString(),
          value: year,
        });
      }
    },

    checkQueryParams() {
      // Pre-select user if user_id is provided in query params
      if (this.$route.query.user_id) {
        this.form.user_id = parseInt(this.$route.query.user_id);
      }
    },

    handleImageUpload(file) {
      if (file) {
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        this.imagePreview = null;
      }
    },

    removeImage() {
      this.form.image = null;
      this.form.image_path = "";
      this.imagePreview = null;
    },

    async saveAward() {
      if (!this.$refs.form.validate()) {
        this.$toast.error("Please fix validation errors");
        return;
      }

      this.saving = true;
      try {
        // Prepare FormData for file upload
        const formData = new FormData();
        
        // Add all form fields
        Object.keys(this.form).forEach((key) => {
          if (key === 'image') {
            // Handle the file separately
            if (this.form.image) {
              formData.append('image_path', this.form.image);
            }
          } else {
            // Add other form data, convert empty strings to null
            const value = this.form[key] === "" ? null : this.form[key];
            if (value !== null) {
              formData.append(key, value);
            }
          }
        });

        await axios.post("/awards", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        this.$toast.success("Award created successfully");
        this.$router.push("/awards");
      } catch (error) {
        console.error("Error creating award:", error);

        if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          // Display validation errors
          const errors = error.response.data.errors;
          Object.keys(errors).forEach((key) => {
            this.$toast.error(errors[key][0]);
          });
        } else {
          this.$toast.error("Failed to create award");
        }
      } finally {
        this.saving = false;
      }
    },

    goBack() {
      this.$router.push("/awards");
    },

    resetForm() {
      this.$refs.form.reset();
      this.form = {
        user_id: null,
        name: "",
        year: new Date().getFullYear(),
        image: null,
        image_path: "",
        is_active: true,
      };
      this.imagePreview = null;
    },
  },
};
</script>

<style scoped>
.v-card {
  border-radius: 20px !important;
}

.v-text-field--outlined >>> fieldset {
  border-radius: 25px;
}

.v-file-input >>> .v-input__control .v-input__slot {
  border-radius: 25px;
}
</style>